/**
 * FitLite Sizer Configuration
 * Module: NovaFitLite
 * Version: 1.3.2
 *
 * This file contains archetype definitions and sizing logic.
 * No external dependencies required.
 *
 * DESIGN PRINCIPLES (LOCKED):
 * - Archetypes represent fit behavior, not garment category
 * - Sleeves and necklines are variants, not behaviors
 * - Use flags for behavioral constraints, not new archetypes
 * - Keep archetype list small and stable
 *
 * v1.3.2 — Arms allocator replaces Length
 *   - The third allocator slot is now "arms" (two values: standard / tight)
 *     instead of "length" (three values). Arms tightness is a more reliable
 *     signal than length for archetypes shoppers actually compare against.
 *   - Boxy/relaxed archetypes intentionally have arms.tight = 0: the cut is
 *     already roomy in the sleeves, so a tight-arm complaint usually means
 *     the shopper needs a different style, not a different size.
 *
 * v1.3.0 — Body Type Allocators
 * Three optional secondary modifiers (torso / shoulder / arms) applied AFTER
 * the height+weight lookup and preference adjustment. Each archetype defines
 * its own per-allocator modifier table; the total is clamped to ±1 so allocators
 * never move more than one size. See applyAllocators() in the public API.
 */

window.FitLiteConfig = (function() {
  'use strict';

  // Template to archetype mapping
  // Long sleeves reuse existing archetypes (sleeve length doesn't affect torso fit)
  // V-necks reuse lightweight_fitted (with disableDownsize flag)
  const TEMPLATE_TO_ARCHETYPE = {
    // Comfort Colors - heavyweight_boxy
    'product.comfort-color-1717': 'heavyweight_boxy',
    'product.comfort-colors-6030': 'heavyweight_boxy',
    'product.comfort-colors-long-sleeve': 'heavyweight_boxy',
    'product.comfort-colors-6014': 'heavyweight_boxy',  // Long sleeve

// Bella+Canvas - lightweight_fitted
'product.bella-3001-crew-neck': 'lightweight_fitted',
'product.bella-3501-long-sleeve': 'lightweight_fitted',
'product.bella-canvas-long-sleeve': 'lightweight_fitted',
'product.bella-3005-vneck': 'lightweight_fitted',
'product.bella-vneck': 'lightweight_fitted',
'product.bella-6400-relaxed': 'lightweight_fitted',


    // Gildan - midweight_standard
    'product.gildan-2000': 'midweight_standard',
    'product.gildan-64000': 'midweight_standard',  // Softstyle tee — the MyDesigns default blank (Teri 2026-06-18)
    'product.gildan-2400': 'midweight_standard',  // Long sleeve
    'product.gildan-long-sleeve': 'midweight_standard',

    // Hoodies - relaxed_hoodie
    'product.gildan-18500-hoodie': 'relaxed_hoodie',

    // Next Level
    'product.next-level-3600': 'midweight_standard'
  };

  /**
   * GARMENT FLAGS
   * Per-template behavioral overrides that apply AFTER archetype detection.
   * Use flags for constraints that don't warrant a new archetype.
   *
   * Available flags:
   * - disableDownsize: Prevents fitted preference (and net-negative allocators)
   *                   from sizing down (V-neck rule)
   */
  const GARMENT_FLAGS = {
    // V-necks: never recommend downsizing (prevents "feels smaller than expected" regret)
    'product.bella-3005-vneck': {
      disableDownsize: true
    },
    'product.bella-vneck': {
      disableDownsize: true
    }
  };

  // Standard size order for comparison
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  /**
   * Archetype Definitions
   * Each archetype defines sizing behavior based on height/weight combinations
   *
   * Structure:
   * - sizeTable: 2D lookup [height range][weight range] → size
   * - heightRanges: [min, max] in total inches
   * - weightRanges: [min, max] in pounds
   * - fittedAdjust: size adjustment for "fitted" preference (-1 = size down)
   * - looserAdjust: size adjustment for "looser" preference (+1 = size up)
   * - allocatorModifiers: per-archetype torso/shoulder/arms modifier tables (v1.3+)
   * - allocatorNotes: keyed "<allocator>.<value>" → contextual result copy (v1.3+)
   */
  const ARCHETYPES = {

    /**
     * HEAVYWEIGHT_BOXY
     * Applies to: Comfort Colors 1717, Comfort Colors 6030
     * Characteristics: Generous cut, relaxed fit, heavyweight cotton
     * Sizing tendency: Runs slightly large, boxy silhouette
     */
    heavyweight_boxy: {
      fittedAdjust: -1,
      looserAdjust: 1,

      // Height ranges in total inches
      // Short: < 5'4" (64"), Average: 5'4"-5'9" (64-69"), Tall: 5'9"-6'1" (69-73"), Very Tall: > 6'1" (73"+)
      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      // Weight ranges in pounds (extended to cover all body types)
      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      // Size lookup table: sizeTable[heightIndex][weightIndex]
      sizeTable: [
        // Short (< 5'4")
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average (5'4" - 5'9")
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall (5'9" - 6'1")
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall (> 6'1")
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Boxy already runs roomy in body and arms — narrow torso looks oversized.
      // Arms.tight is 0 here: the cut is already generous in the sleeves, so a
      // tight-arms complaint signals a different style, not a different size.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: -1 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'torso.narrow_torso':  'This style runs boxy — sizing down keeps the silhouette clean.',
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    },

    /**
     * MIDWEIGHT_STANDARD
     * Applies to: Gildan 2000, Next Level 3600
     * Characteristics: True-to-size, standard fit
     * Sizing tendency: Predictable, middle-of-road fit
     */
    midweight_standard: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        // Short
        ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // True-to-size: fuller torso wants +1; tight-arms also wants +1.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 1, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 1 }
      },
      allocatorNotes: {
        'torso.full_torso':    'True-to-size styles can pull through the middle — sizing up keeps it easy.',
        'shoulder.broad':      'Sizing up keeps the shoulders from pulling.',
        'arms.tight':          'Sizing up gives more room through the arms and shoulders.'
      }
    },

    /**
     * LIGHTWEIGHT_FITTED
     * Applies to: Bella+Canvas 3001
     * Characteristics: Slim cut, lightweight, fashion-forward
     * Sizing tendency: Runs slim, shorter body length
     */
    lightweight_fitted: {
      fittedAdjust: 0,  // Already fitted, no adjustment
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

weightRanges: [
  { label: 'light', min: 0, max: 120 },
  { label: 'medium', min: 120, max: 150 },
  { label: 'athletic', min: 150, max: 180 },
  { label: 'solid', min: 180, max: 210 },
  { label: 'heavy', min: 210, max: 250 },
  { label: 'very_heavy', min: 250, max: 310 },
  { label: 'extra_heavy', min: 310, max: 390 },
  { label: 'extreme', min: 390, max: 99999 }
],


      sizeTable: [
        // Short - size up slightly due to slim fit
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'M', 'L', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        // Very Tall
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Slim cut: full_torso, broad shoulder, and tight arms all push +1.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 1, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 1 }
      },
      allocatorNotes: {
        'torso.full_torso':    'Slim-cut styles run straight through the body — sizing up keeps it comfortable.',
        'shoulder.broad':      'Slim cut at the shoulder — sizing up prevents pulling.',
        'arms.tight':          'Bella+Canvas styles run slim in the sleeves — sizing up gives you more room through the arms.'
      }
    },

    /**
     * RELAXED_HOODIE
     * Applies to: Gildan 18500, Champion hoodies
     * Characteristics: Oversized, cozy, layering piece
     * Sizing tendency: Already relaxed, generous cut
     */
    relaxed_hoodie: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        // Short - hoodies run big
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Average
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Tall
        ['M', 'M', 'L', 'L', 'XL', '2XL', '3XL', '5XL'],
        // Very Tall
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Already oversized: narrow_torso sizes down; broad shoulder still +1.
      // Arms.tight is 0: hoodies are already generous in the sleeves.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: -1 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'torso.narrow_torso':  'Hoodies run roomy — sizing down keeps the silhouette clean.',
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    },

    /**
     * UNISEX_LOOSE_CUT
     * Applies to: Generic unisex styles
     * Characteristics: Accommodating fit for all body types
     */
    unisex_loose_cut: {
      fittedAdjust: -1,
      looserAdjust: 1,

      heightRanges: [
        { label: 'short', min: 0, max: 64 },
        { label: 'average', min: 64, max: 69 },
        { label: 'tall', min: 69, max: 73 },
        { label: 'very_tall', min: 73, max: 999 }
      ],

      weightRanges: [
        { label: 'light', min: 0, max: 130 },
        { label: 'medium', min: 130, max: 160 },
        { label: 'athletic', min: 160, max: 190 },
        { label: 'solid', min: 190, max: 220 },
        { label: 'heavy', min: 220, max: 260 },
        { label: 'very_heavy', min: 260, max: 320 },
        { label: 'extra_heavy', min: 320, max: 400 },
        { label: 'extreme', min: 400, max: 99999 }
      ],

      sizeTable: [
        ['S', 'S', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        ['S', 'M', 'M', 'L', 'XL', '2XL', '3XL', '5XL'],
        ['M', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
        ['M', 'L', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
      ],

      // Accommodating cut: light touch, only shoulder nudges the result.
      // Arms.tight is 0: the loose cut is already generous in the sleeves.
      allocatorModifiers: {
        torso:    { balanced: 0, full_torso: 0, narrow_torso: 0 },
        shoulder: { standard: 0, broad: 1,      narrow: -1 },
        arms:     { standard: 0, tight: 0 }
      },
      allocatorNotes: {
        'shoulder.broad':      'Sizing up gives shoulder room without binding.'
      }
    }
  };

  // Public API
  return {
    TEMPLATE_TO_ARCHETYPE: TEMPLATE_TO_ARCHETYPE,
    GARMENT_FLAGS: GARMENT_FLAGS,
    SIZE_ORDER: SIZE_ORDER,
    ARCHETYPES: ARCHETYPES,

    /**
     * Get archetype from template name
     */
    getArchetype: function(templateName) {
      return TEMPLATE_TO_ARCHETYPE[templateName] || null;
    },

    /**
     * Get archetype configuration
     */
    getArchetypeConfig: function(archetypeName) {
      return ARCHETYPES[archetypeName] || null;
    },

    /**
     * Get garment flags for a template
     * Returns flags object or empty object if no flags defined
     */
    getGarmentFlags: function(templateName) {
      return GARMENT_FLAGS[templateName] || {};
    },

    /**
     * Compare two sizes, returns: -1 if a < b, 0 if equal, 1 if a > b
     */
    compareSize: function(sizeA, sizeB) {
      const indexA = SIZE_ORDER.indexOf(sizeA);
      const indexB = SIZE_ORDER.indexOf(sizeB);
      if (indexA < indexB) return -1;
      if (indexA > indexB) return 1;
      return 0;
    },

    /**
     * Adjust size by steps (positive = size up, negative = size down)
     * Respects disableDownsize flag when provided
     */
    adjustSize: function(size, steps, flags) {
      flags = flags || {};
      const currentIndex = SIZE_ORDER.indexOf(size);
      if (currentIndex === -1) return size;

      // If disableDownsize flag is set, prevent negative adjustments
      if (flags.disableDownsize && steps < 0) {
        return size;
      }

      const newIndex = Math.max(0, Math.min(SIZE_ORDER.length - 1, currentIndex + steps));
      return SIZE_ORDER[newIndex];
    },

    /**
     * Apply Body Type Allocators (v1.3+)
     *
     * Sums per-archetype torso/shoulder/arms modifiers, clamps the total to ±1
     * (allocators may never move more than one size from baseline), respects
     * disableDownsize, and returns the first matching contextual note for an
     * allocator that actually contributed a non-zero modifier.
     *
     * @param {string} baseSize         Already preference-adjusted size (e.g. 'L')
     * @param {string} archetypeName    Archetype key (e.g. 'lightweight_fitted')
     * @param {object} bodyType         { torso, shoulder, arms } — neutral values
     *                                  ('balanced', 'standard', 'standard') yield no change
     * @param {object} flags            Garment flags (disableDownsize honored)
     * @returns {{adjustedSize: string, note: (string|null)}}
     */
    applyAllocators: function(baseSize, archetypeName, bodyType, flags) {
      flags = flags || {};
      bodyType = bodyType || {};

      const archetype = ARCHETYPES[archetypeName];
      if (!archetype || !archetype.allocatorModifiers) {
        return { adjustedSize: baseSize, note: null };
      }

      const mods = archetype.allocatorModifiers;
      const torsoMod    = (mods.torso    && mods.torso[bodyType.torso])       || 0;
      const shoulderMod = (mods.shoulder && mods.shoulder[bodyType.shoulder]) || 0;
      const armsMod     = (mods.arms     && mods.arms[bodyType.arms])         || 0;

      // Clamp total: allocators must never move more than one size in either direction
      let total = Math.max(-1, Math.min(1, torsoMod + shoulderMod + armsMod));

      // disableDownsize: suppress any net-negative allocator move (V-neck rule)
      if (flags.disableDownsize && total < 0) {
        total = 0;
      }

      const currentIndex = SIZE_ORDER.indexOf(baseSize);
      if (currentIndex === -1) {
        return { adjustedSize: baseSize, note: null };
      }

      const newIndex = Math.max(0, Math.min(SIZE_ORDER.length - 1, currentIndex + total));
      const adjustedSize = SIZE_ORDER[newIndex];

      // Note fires only when the recommendation actually moved. This avoids
      // misleading copy when modifiers cancel out, get zeroed by disableDownsize,
      // or hit the XS/5XL boundary clamps.
      let note = null;
      if (newIndex !== currentIndex) {
        const notes = archetype.allocatorNotes || {};
        const order = [
          ['torso',    bodyType.torso,    torsoMod],
          ['shoulder', bodyType.shoulder, shoulderMod],
          ['arms',     bodyType.arms,     armsMod]
        ];
        for (let i = 0; i < order.length; i++) {
          const key = order[i][0];
          const value = order[i][1];
          const mod = order[i][2];
          if (mod === 0 || !value) continue;
          const noteKey = key + '.' + value;
          if (notes[noteKey]) {
            note = notes[noteKey];
            break;
          }
        }
      }

      return { adjustedSize: adjustedSize, note: note };
    }
  };

  /*
   * v1.3.2 logic trace — verification
   *
   * Test case: 5'6" / 160 lbs / Standard preference / lightweight_fitted / arms=tight
   *   1. heightInches = (5 * 12) + 6 = 66
   *   2. lightweight_fitted.heightRanges → index 1 'average' (64–69)
   *   3. lightweight_fitted.weightRanges → 160 falls in 'athletic' (150–180), index 2
   *   4. sizeTable[1][2] = 'L'  (base size)
   *   5. preference = 'standard' → no fittedAdjust applied. baseSize stays 'L'.
   *   6. applyAllocators('L', 'lightweight_fitted',
   *                      { torso: 'balanced', shoulder: 'standard', arms: 'tight' }, {})
   *      - torsoMod    = .torso.balanced  = 0
   *      - shoulderMod = .shoulder.standard = 0
   *      - armsMod     = .arms.tight = +1
   *      - total = clamp(+1, -1, +1) = +1
   *      - flags.disableDownsize = false → no suppression
   *      - SIZE_ORDER.indexOf('L') = 3 → newIndex = 4 → 'XL'
   *      - first non-zero allocator with a note: arms.tight →
   *        "Bella+Canvas styles run slim in the sleeves — sizing up gives you more room through the arms."
   *      - returns { adjustedSize: 'XL', note: <above> }
   *   7. Final recommendation: 'XL' with the slim-sleeves note.
   *
   * Boxy "no-op for arms" check (v1.3.2 design intent):
   *   archetype = heavyweight_boxy, bodyType.arms = 'tight'
   *   → arms.tight modifier is 0 (boxy is already roomy in sleeves)
   *   → if torso/shoulder are also neutral, output is unchanged and no note fires.
   *
   * Neutral-baseline check (regression guard):
   *   bodyType = { torso: 'balanced', shoulder: 'standard', arms: 'standard' }
   *   → all three mods = 0, total = 0, adjustedSize === baseSize, note = null.
   */
})();
